/**
 * UrbanEntityDetailsPanel — Panel derecho de detalle de una urban_entity
 *
 * @remarks
 * Muestra todos los campos de la entidad: tipo, status, coordenadas,
 * trazabilidad (source, batch), attributes JSONB y sección NPA.
 *
 * La sección NPA usa PointAssociationSection. Si el entityType no está
 * en POINT_ENTITY_REGISTRY, PointAssociationSection devuelve null (sin error).
 * Para Fase 2A esto es aceptable: NPA visible para tipos registrados,
 * ausente para tipos aún no registrados.
 *
 * @module urban-inventory/components
 */

import { X, MapPin } from 'lucide-react';
import { PointAssociationSection } from '../../../spatial/components/PointAssociationSection';
import { UrbanEntityAttributesView } from './UrbanEntityAttributesView';
import type { UrbanEntity } from '../types/urban-entity.types';

const STATUS_LABELS: Record<string, string> = {
  active: 'Activo',
  inactive: 'Inactivo',
  removed: 'Removido',
  pending_review: 'En revisión',
};

const SOURCE_TYPE_LABELS: Record<string, string> = {
  observed: 'Observado',
  declared: 'Declarado',
  imported: 'Importado',
  estimated: 'Estimado',
  inferred: 'Inferido',
  synthetic: 'Sintético',
};

const TYPE_LABELS: Record<string, string> = {
  tree: 'Árbol',
  streetlight: 'Luminaria',
  waste_container: 'Contenedor',
  transit_stop: 'Parada de transporte',
  bench: 'Banco urbano',
  traffic_light: 'Semáforo',
  fountain: 'Fuente',
  waste_depot: 'Depósito',
  waste_hotspot: 'Microbasural',
  building_centroid: 'Edificio (centroide)',
};

interface UrbanEntityDetailsPanelProps {
  entity: UrbanEntity;
  projectId: string;
  onClose: () => void;
  /** Mostrar sección de asociación a red (NPA). Default: true. Requiere contexto de mapa. */
  showNpa?: boolean;
}

export function UrbanEntityDetailsPanel({
  entity,
  projectId,
  onClose,
  showNpa = true,
}: UrbanEntityDetailsPanelProps): JSX.Element {
  const [lng, lat] = entity.geometry.coordinates;
  const entityLabel = TYPE_LABELS[entity.entityType] ?? entity.entityType;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
        fontSize: '13px',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 16px',
          borderBottom: '1px solid var(--color-border, #e2e8f0)',
          flexShrink: 0,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <MapPin size={14} />
          <span style={{ fontWeight: 600, color: 'var(--color-text, #1e293b)' }}>
            {entityLabel}
          </span>
        </div>
        <button
          type="button"
          onClick={onClose}
          style={{
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            padding: '2px',
            color: 'var(--color-text-muted, #94a3b8)',
          }}
          aria-label="Cerrar"
        >
          <X size={16} />
        </button>
      </div>

      {/* Contenido scrollable */}
      <div style={{ flex: 1, overflow: 'auto', padding: '12px 16px' }}>

        {/* Sección: identificación */}
        <Section title="Identificación">
          <Field label="Tipo actual" value={entityLabel} />
          {entity.originalEntityType && (
            <Field
              label="Tipo original"
              value={TYPE_LABELS[entity.originalEntityType] ?? entity.originalEntityType}
              muted
            />
          )}
          <Field
            label="Estado"
            value={STATUS_LABELS[entity.status] ?? entity.status}
            valueStyle={{ color: entity.status === 'active' ? '#22c55e' : entity.status === 'removed' ? '#ef4444' : undefined }}
          />
        </Section>

        {/* Sección: geometría */}
        <Section title="Geometría">
          <Field label="Longitud" value={lng.toFixed(6)} />
          <Field label="Latitud" value={lat.toFixed(6)} />
        </Section>

        {/* Sección: trazabilidad */}
        <Section title="Trazabilidad">
          {entity.sourceType && (
            <Field label="Tipo de fuente" value={SOURCE_TYPE_LABELS[entity.sourceType] ?? entity.sourceType} />
          )}
          {entity.sourceMethod && (
            <Field label="Método" value={entity.sourceMethod} />
          )}
          {entity.dataSource && (
            <Field label="Origen de dato" value={entity.dataSource} />
          )}
          {entity.confidenceScore !== null && (
            <Field label="Confianza" value={`${(entity.confidenceScore * 100).toFixed(0)}%`} />
          )}
          {entity.importBatchId && (
            <Field label="Lote de importación" value={entity.importBatchId} mono />
          )}
          <Field label="Creado" value={formatDate(entity.createdAt)} muted />
          <Field label="Actualizado" value={formatDate(entity.updatedAt)} muted />
          {entity.reclassifiedAt && (
            <Field label="Reclasificado" value={formatDate(entity.reclassifiedAt)} muted />
          )}
        </Section>

        {/* Sección: atributos */}
        <Section title="Atributos">
          <UrbanEntityAttributesView attributes={entity.attributes} />
        </Section>

        {/* Sección: asociación a red (requiere contexto de mapa) */}
        {showNpa && (
          <PointAssociationSection
            projectId={projectId}
            entityType={entity.entityType}
            entityId={entity.id}
            coordinates={[lng, lat]}
          />
        )}

      </div>
    </div>
  );
}

// ─── Helpers internos ─────────────────────────────────────────────────────────

function Section({ title, children }: { title: string; children: React.ReactNode }): JSX.Element {
  return (
    <div style={{ marginBottom: '16px' }}>
      <h3
        style={{
          margin: '0 0 6px',
          fontSize: '10px',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color: 'var(--color-text-muted, #94a3b8)',
        }}
      >
        {title}
      </h3>
      {children}
    </div>
  );
}

interface FieldProps {
  label: string;
  value: string;
  muted?: boolean;
  mono?: boolean;
  valueStyle?: React.CSSProperties;
}

function Field({ label, value, muted, mono, valueStyle }: FieldProps): JSX.Element {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        padding: '3px 0',
        borderBottom: '1px solid var(--color-border-subtle, #f1f5f9)',
        gap: '8px',
      }}
    >
      <span style={{ color: 'var(--color-text-secondary, #64748b)', flexShrink: 0 }}>
        {label}
      </span>
      <span
        style={{
          color: muted ? 'var(--color-text-muted, #94a3b8)' : 'var(--color-text, #1e293b)',
          fontFamily: mono ? 'monospace' : undefined,
          fontSize: mono ? '11px' : undefined,
          textAlign: 'right',
          wordBreak: 'break-all',
          ...valueStyle,
        }}
      >
        {value}
      </span>
    </div>
  );
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateStr;
  }
}
